<div class="package-links">
    <div class="package-links-header">
        <div>
            <h4>{{ t()('links.title') }}</h4>
            <p class="hint">{{ t()('links.intro') }}</p>
        </div>
        <p-button [label]="t()('links.new')" icon="pi pi-link" (onClick)="openCreate()" [disabled]="loading()" />
    </div>

    @if (loading()) {
        <div class="flex justify-content-center p-3">
            <p-progressspinner [style]="{ width: '2rem', height: '2rem' }" />
        </div>
    } @else if (loadError()) {
        <app-empty-state tone="error" [title]="t()('links.loadError')" [actionLabel]="t()('links.retry')" (action)="load()" [compact]="true" />
    } @else if (links().length === 0) {
        <app-empty-state icon="pi pi-link" [title]="t()('links.none')" [description]="t()('links.noneHint')" [compact]="true" />
    } @else {
        <p-table [value]="links()" class="management-table package-links-table" [scrollable]="true" dataKey="id">
            <ng-template #header>
                <tr>
                    <th>{{ t()('links.columnLink') }}</th>
                    <th>{{ t()('links.target') }}</th>
                    <th>{{ t()('links.columnCreated') }}</th>
                    <th>{{ t()('links.columnExpires') }}</th>
                    <th>{{ t()('links.columnUses') }}</th>
                    <th>{{ t()('links.columnLastUsed') }}</th>
                    <th style="width: 9.5rem"></th>
                </tr>
            </ng-template>
            <ng-template #body let-link>
                <tr [class.package-link-expired]="isExpired(link)">
                    <td>
                        <div class="link-cell">
                            <a class="link-url" [href]="url(link)" target="_blank" rel="noopener">{{ url(link) }}</a>
                            @if (link.label) {
                                <span class="link-label">{{ link.label }}</span>
                            }
                            @if (link.notes) {
                                <span class="link-notes"><i class="pi pi-file-edit"></i>{{ t()('links.hasNotes') }}</span>
                            }
                            <span class="link-window">{{ fill('links.window', { minutes: link.downloadWindowMinutes }) }}</span>
                        </div>
                    </td>
                    <td>
                        <p-tag [value]="targetLabel(link)" [severity]="link.target === 'latest' ? 'success' : 'info'" [rounded]="true" />
                    </td>
                    <td>{{ formatDate(link.createdUtc) }}</td>
                    <td>
                        @if (isExpired(link)) {
                            <p-tag [value]="t()('links.expired')" severity="danger" [rounded]="true" />
                        } @else {
                            {{ formatDate(link.expiresUtc) }}
                        }
                    </td>
                    <td>{{ link.accessCount }}</td>
                    <td>{{ formatDate(link.lastAccessedUtc) }}</td>
                    <td>
                        <div class="row-actions">
                            <p-button icon="pi pi-copy" [text]="true" [rounded]="true" size="small" (onClick)="copy(link)" [pTooltip]="t()('links.copyUrl')" [ariaLabel]="t()('links.copyUrl')" />
                            <p-button icon="pi pi-pencil" [text]="true" [rounded]="true" size="small" (onClick)="openEdit(link)" [pTooltip]="t()('links.edit')" [ariaLabel]="t()('links.edit')" />
                            <p-button icon="pi pi-ban" [text]="true" [rounded]="true" size="small" severity="danger" (onClick)="revoke(link)" [pTooltip]="t()('links.revoke')" [ariaLabel]="t()('links.revoke')" />
                        </div>
                    </td>
                </tr>
            </ng-template>
        </p-table>
    }
</div>

<p-dialog [header]="t()('links.new')" [visible]="showCreateDialog()" (visibleChange)="!$event && closeCreate()" [modal]="true" [style]="{ width: '600px', maxWidth: '95vw' }" class="package-link-dialog" [closable]="true">
    @if (createdLink(); as link) {
        <p-message severity="success">{{ t()('links.ready') }}</p-message>
        <div class="field mt-3">
            <label>{{ t()('links.columnLink') }}</label>
            <div class="console-command-block">
                <pre><code>{{ createdUrl() }}</code></pre>
                <p-button icon="pi pi-copy" [text]="true" [rounded]="true" size="small" class="console-command-copy" (onClick)="copy(link)" [pTooltip]="t()('links.copyUrl')" [ariaLabel]="t()('links.copyUrl')" />
            </div>
        </div>
        <div class="flex flex-wrap gap-2">
            <p-button [label]="t()('links.copyUrl')" icon="pi pi-copy" (onClick)="copy(link)" />
            <a class="p-button p-button-outlined" [href]="createdUrl()" target="_blank" rel="noopener"><i class="pi pi-external-link mr-2"></i>{{ t()('links.open') }}</a>
        </div>
    } @else {
        <div class="field">
            <label>{{ t()('links.target') }}</label>
            <div class="target-options">
                <label class="target-option" [class.disabled]="!canPinSelected()">
                    <p-radiobutton name="linkTarget" value="pinned" [ngModel]="target()" (ngModelChange)="target.set($event)" [disabled]="!canPinSelected()" inputId="linkTargetPinned" />
                    <span class="target-text">
                        <span class="target-title">{{ t()('links.targetPinned') }}</span>
                        <span class="target-detail">{{ fill('links.targetPinnedDetail', { version: pinnedVersion()?.assemblyVersion ?? '—' }) }}</span>
                    </span>
                </label>
                <label class="target-option" [class.disabled]="!canUseLatest()">
                    <p-radiobutton name="linkTarget" value="latest" [ngModel]="target()" (ngModelChange)="target.set($event)" [disabled]="!canUseLatest()" inputId="linkTargetLatest" />
                    <span class="target-text">
                        <span class="target-title">{{ t()('links.targetLatest') }}</span>
                        <span class="target-detail">{{ canUseLatest() ? fill('links.targetLatestDetail', { version: latestVersion()!.assemblyVersion }) : t()('links.latestUnavailable') }}</span>
                    </span>
                </label>
            </div>
        </div>
        @if (target() === 'pinned') {
            <div class="field">
                <p-select
                    inputId="linkVersion"
                    [options]="versionOptions()"
                    optionLabel="assemblyVersion"
                    dataKey="id"
                    [ngModel]="pinnedVersion()"
                    (ngModelChange)="pinnedVersion.set($event)"
                    [filter]="true"
                    [filterPlaceholder]="t()('links.versionSearch')"
                    (onFilter)="searchVersions($event.filter)"
                    [loading]="versionsLoading()"
                    [placeholder]="versionsLoading() ? t()('links.versionsLoading') : ''"
                    appendTo="body"
                    class="w-full"
                />
            </div>
        }
        <div class="field">
            <label for="linkLabel">{{ t()('links.label') }}</label>
            <input id="linkLabel" type="text" pInputText maxlength="100" [placeholder]="t()('links.labelPlaceholder')" [ngModel]="label()" (ngModelChange)="label.set($event)" class="w-full" />
        </div>
        <div class="field">
            <label for="linkNotes">{{ t()('links.notes') }}</label>
            <app-markdown-editor
                inputId="linkNotes"
                [(value)]="notes"
                [rows]="5"
                [maxLength]="maxNotesLength"
                [placeholder]="t()('links.notesPlaceholder')"
                [hint]="t()('links.notesHint')"
                [writeLabel]="t()('links.write')"
                [previewLabel]="t()('links.preview')"
            />
        </div>
        <div class="field">
            <label for="linkExpiry">{{ t()('links.expiry') }}</label>
            <p-datepicker inputId="linkExpiry" [ngModel]="expiresAt()" (ngModelChange)="expiresAt.set($event)" [showTime]="true" [showIcon]="true" [showClear]="true" [minDate]="minExpiry" dateFormat="yy-mm-dd" [placeholder]="t()('links.never')" [appendTo]="'body'" class="w-full" />
            <small class="hint">{{ t()('links.expiryHint') }}</small>
        </div>
        <div class="field">
            <label for="linkWindow">{{ t()('links.downloadWindow') }}</label>
            <p-select inputId="linkWindow" [options]="downloadWindowOptions" optionLabel="label" optionValue="value" [ngModel]="downloadWindow()" (ngModelChange)="downloadWindow.set($event)" appendTo="body" class="w-full" />
            <small class="hint">{{ t()('links.downloadWindowHint') }}</small>
        </div>
        @if (createError()) {
            <p-message severity="error">{{ t()('links.createError') }}</p-message>
        }
    }
    <ng-template #footer>
        @if (createdLink()) {
            <p-button [label]="t()('links.close')" [text]="true" (onClick)="closeCreate()" />
        } @else {
            <p-button [label]="t()('links.cancel')" [text]="true" (onClick)="closeCreate()" [disabled]="creating()" />
            <p-button [label]="t()('links.create')" icon="pi pi-link" (onClick)="create()" [disabled]="!canCreate()" [loading]="creating()" />
        }
    </ng-template>
</p-dialog>

<p-dialog [header]="t()('links.edit')" [visible]="!!editingLink()" (visibleChange)="!$event && closeEdit()" [modal]="true" [style]="{ width: '640px', maxWidth: '95vw' }" class="package-link-dialog" [closable]="!saving()">
    @if (editingLink(); as link) {
        <div class="field">
            <label>{{ t()('links.columnLink') }}</label>
            <div class="link-edit-url">{{ url(link) }}</div>
        </div>
        <div class="field">
            <label for="editLinkLabel">{{ t()('links.label') }}</label>
            <input id="editLinkLabel" type="text" pInputText maxlength="100" [placeholder]="t()('links.labelPlaceholder')" [ngModel]="editLabel()" (ngModelChange)="editLabel.set($event)" class="w-full" />
        </div>
        <div class="field">
            <label for="editLinkNotes">{{ t()('links.notes') }}</label>
            <app-markdown-editor
                inputId="editLinkNotes"
                [(value)]="editNotes"
                [rows]="10"
                [maxLength]="maxNotesLength"
                [disabled]="saving()"
                [placeholder]="t()('links.notesPlaceholder')"
                [hint]="t()('links.notesHint')"
                [writeLabel]="t()('links.write')"
                [previewLabel]="t()('links.preview')"
                (submit)="saveEdit()"
            />
        </div>
        @if (saveError()) {
            <p-message severity="error">{{ t()('links.saveError') }}</p-message>
        }
    }
    <ng-template #footer>
        <p-button [label]="t()('links.cancel')" [text]="true" (onClick)="closeEdit()" [disabled]="saving()" />
        <p-button [label]="t()('links.save')" icon="pi pi-check" (onClick)="saveEdit()" [loading]="saving()" [disabled]="editNotes().length > maxNotesLength" />
    </ng-template>
</p-dialog>
